/**
 * Metrics Grid Component
 *
 * Renders the grid of metric boxes with appropriate formatting
 * based on metric type (Sales, Bookings, etc.).
 */

import React from 'react';
import clsx from 'clsx';
import { MetricBox } from '../../UI/V3/MetricBox/MetricBox';
import type { MetricData } from '../types/DashboardTypes';
import { isSalesMetric, isBookingsMetric } from '../types/DashboardTypes';
import styles from '../DashboardComponent.module.scss';

interface MetricsGridProps {
    metrics: MetricData[];
    isLoading?: boolean;
    className?: string;
}

export const MetricsGrid: React.FC<MetricsGridProps> = ({ metrics, isLoading, className }) => {
    const renderMetricValue = (metric: MetricData) => (
        <div className={styles.metricValue}>
            <div className={styles.mainValue}>
                {metric.value}
                {(isSalesMetric(metric) || isBookingsMetric(metric)) && (
                    <span
                        className={clsx(
                            styles.changePercent,
                            metric.change.startsWith('+') ? styles.positive : styles.negative
                        )}
                    >
                        {metric.change}
                    </span>
                )}
            </div>
            {isSalesMetric(metric) && <div className={styles.avgPrice}>Avg price {metric.avgPrice}</div>}
            {isBookingsMetric(metric) && metric.perDay && <div className={styles.avgPrice}>{metric.perDay}</div>}
        </div>
    );

    return (
        <div className={clsx(styles.metricsGrid, className)}>
            {metrics.map((metric, index) => (
                <MetricBox
                    key={`${metric.title}-${index}`}
                    title={metric.title}
                    value={renderMetricValue(metric)}
                    isLoading={isLoading}
                    className={styles.metricBox}
                />
            ))}
        </div>
    );
};
